import { useCallback, useEffect, useRef, useState } from 'react';
import { JSONObject, MultiSelectMode } from '../../../types/types';
import { FormTokenNode } from '../../../types/context';
import { resolveFormItemDecorators } from '../utils/resolveFormItemDecorators';
import { getFormTypeFromPathname } from '../../../utils/forms/getFormTypeFromPathname';

const RESOLVE_DEBOUNCE_MS = 250;

type UseFormItemDecoratorsOptions = {
  formData: Record<string, any> | null;
  actualToken: FormTokenNode | null;
  pathname: string;
  multiSelectMode: MultiSelectMode;
};

type UseFormItemDecoratorsResult = {
  formItems: any[];
  initializeFormItems: (
    template: any[],
    initialFormData: JSONObject
  ) => Promise<void>;
};

export const useFormItemDecorators = ({
  formData,
  actualToken,
  pathname,
  multiSelectMode,
}: UseFormItemDecoratorsOptions): UseFormItemDecoratorsResult => {
  const [formItems, setFormItems] = useState<any[]>([]);
  // Unresolved decorator templates — kept verbatim so each recompute re-evaluates JSONata against fresh form data.
  const templateRef = useRef<any[]>([]);
  // Monotonic token: latest recompute wins; stale async results are dropped.
  const tokenRef = useRef(0);
  // First effect run skips the debounce so the post-fetch resolution is instant; subsequent typing-driven runs wait.
  const isFirstRun = useRef(true);

  const buildContext = useCallback(
    (formSnapshot: JSONObject): JSONObject => ({
      formType:
        (actualToken?.meta?.requestData?.['formType'] as string | undefined) ??
        getFormTypeFromPathname(pathname),
      mode: multiSelectMode,
      form: formSnapshot,
    }),
    [actualToken, pathname, multiSelectMode]
  );

  const resolveAndCommit = useCallback(
    async (formSnapshot: JSONObject) => {
      const template = templateRef.current;
      if (!template.length) {
        return;
      }

      tokenRef.current += 1;
      const thisCallToken = tokenRef.current;

      const resolved = await resolveFormItemDecorators(
        template,
        buildContext(formSnapshot)
      );

      const isStale = thisCallToken !== tokenRef.current;
      if (isStale) {
        return;
      }

      setFormItems(resolved);
    },
    [buildContext]
  );

  const initializeFormItems = useCallback(
    async (template: any[], initialFormData: JSONObject) => {
      templateRef.current = template;
      await resolveAndCommit(initialFormData);
    },
    [resolveAndCommit]
  );

  useEffect(() => {
    if (!formData) {
      return;
    }

    const delay = isFirstRun.current ? 0 : RESOLVE_DEBOUNCE_MS;
    isFirstRun.current = false;

    const timeoutId = setTimeout(() => {
      resolveAndCommit(formData as JSONObject);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [formData, resolveAndCommit]);

  return { formItems, initializeFormItems };
};
